import { onMounted, onUnmounted, Ref, ref } from 'vue'

type Direction = 'left' | 'right'

interface ParamsObj {
  ref: Ref<HTMLElement | null>
  widthProperty: string
  elementGetter?: (refElement: Ref<HTMLElement | null>) => HTMLElement
  direction?: Direction
  disableResizeWidth?: boolean
}

const defaultBorderColor = 'rgba(0, 0, 0, 0.1)'
const hoverBorderColor = 'rgba(0, 0, 0, 0.3)'
const HEIGHT_DIFF_THRESHOLD = 10
const WIDTH_DIFF_THRESHOLD = 10

export const useResizeableElement = ({
  ref,
  widthProperty,
  elementGetter,
  direction,
  disableResizeWidth = false
}: ParamsObj) => {
  const setEvents = (el: HTMLElement) => {
    if (!el) return
    const resizeWidth = disableResizeWidth ? null : resizeWidthHandler(el, widthProperty, direction)
    const resizeHeight = resizeHeightHandler(el, widthProperty)
    const mouseDown = mouseDownHandler(el, resizeHeight, resizeWidth)
    const mouseUp = mouseUpHandler(el, resizeHeight, resizeWidth)
    const mouseover = mouseoverHandler(el)

    el.addEventListener('mouseover', mouseover, false)
    el.addEventListener('mousedown', mouseDown, false)
    document.addEventListener('mouseup', mouseUp, false)
  }

  const removeEvents = (el: HTMLElement) => {
    if (!el) return
    const resizeWidth = disableResizeWidth ? null : resizeWidthHandler(el, widthProperty, direction)
    const resizeHeight = resizeHeightHandler(el, widthProperty)
    const mouseDown = mouseDownHandler(el, resizeHeight, resizeWidth)
    const mouseUp = mouseUpHandler(el, resizeHeight, resizeWidth)
    const mouseover = mouseoverHandler(el)

    el.removeEventListener('mouseover', mouseover, false)
    el.removeEventListener('mousedown', mouseDown, false)
    document.removeEventListener('mouseup', mouseUp, false)
  }

  const mouseDownHandler =
    (el: HTMLElement, resizeHeight: (e: MouseEvent) => void, resizeWidth) => (e: MouseEvent) => {
      if (isOverBorderWidth(el, e)) {
        el.style.transition = 'initial'
        document.addEventListener('mousemove', resizeWidth, false)
      } else if (isOverBorderHeight(el, e)) {
        el.style.transition = 'initial'
        document.addEventListener('mousemove', resizeHeight, false)
      }
    }

  const mouseUpHandler =
    (el: HTMLElement, resizeHeight: (e: MouseEvent) => void, resizeWidth) => () => {
      el.style.transition = ''
      document.body.style.cursor = 'default'
      document.removeEventListener('mousemove', resizeHeight, false)
      document.removeEventListener('mousemove', resizeWidth, false)
      el.style.borderBottomColor = defaultBorderColor
      el.style.borderRightColor = defaultBorderColor
    }

  const resizeWidthHandler = (el: HTMLElement, property: string, direction) => (e: MouseEvent) => {
    const store = useProjectStore()
    document.body.style.cursor = isOverBorderWidth(el, e) ? 'ew-resize' : ''
    const calculatedWidth =
      direction === 'left'
        ? Math.abs(el.getBoundingClientRect().right - e.clientX)
        : Math.abs(e.clientX - el.getBoundingClientRect().left)
    if (calculatedWidth < store[property].minWidth || calculatedWidth > store[property].maxWidth) {
      return
    }
    store[property].width = calculatedWidth
  }

  const resizeHeightHandler = (el: HTMLElement, property: string) => (e: MouseEvent) => {
    const store = useProjectStore()
    document.body.style.cursor = isOverBorderHeight(el, e) ? 'ns-resize' : ''
    if (isOverBorderHeight(el, e)) {
      const calculatedHeight = Math.abs(e.clientY - el.getBoundingClientRect().top)
      store[property].height =
        calculatedHeight < store[property].minHeight ? store[property].minHeight : calculatedHeight

      if (calculatedHeight >= window.innerHeight - 200) {
        store[property].height = window.innerHeight - 200
      }
    }
  }

  const isOverBorderWidth = (el: HTMLElement, e: MouseEvent) => {
    const rect = el.getBoundingClientRect()
    const mouseX = e.clientX - rect.left
    return mouseX >= rect.width - WIDTH_DIFF_THRESHOLD
  }

  const isOverBorderHeight = (el: HTMLElement, e: MouseEvent) => {
    const rect = el.getBoundingClientRect()
    const mouseY = e.clientY - rect.top
    return mouseY >= rect.height - HEIGHT_DIFF_THRESHOLD
  }

  const mouseoverHandler = (el: HTMLElement) => (e: MouseEvent) => {
    const isResizeHeight = isOverBorderHeight(el, e)
    const isResizeWidth = isOverBorderWidth(el, e)
    document.body.style.cursor =
      !disableResizeWidth && isResizeWidth ? 'ew-resize' : isResizeHeight ? 'ns-resize' : ''

    el.style.borderBottomColor = isResizeHeight ? hoverBorderColor : defaultBorderColor
    el.style.borderRightColor = isResizeWidth ? hoverBorderColor : defaultBorderColor
  }

  onMounted(() => {
    if (typeof elementGetter == 'function') {
      ref.value = elementGetter(ref.value)
    }
    setEvents(ref.value)
  })

  onUnmounted(() => {
    if (typeof elementGetter == 'function') {
      ref.value = elementGetter(ref.value)
    }
    removeEvents(ref.value)
  })
}

// for refs that are vuetify components
export const vDrawerElementGetter = (refElement: ref<HTMLElement | null>): HTMLElement => {
  if (refElement === null) {
    return null
  }
  return refElement.$el.nextSibling
}
